import { Cancel, CheckCircle, Print } from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Card, CardBody, CardHeader, Col, Modal, Row } from "reactstrap";
import { getAgreement } from "../../api";
import StyledButton from "../../components/Common/StyledButton";
import ApproveConfirm from "../../components/CustomComponents/ApproveConfirm";
import RequirePermission from "../../routes/middleware/requirePermission";
import RequireUserType from "../../routes/middleware/requireUserType";
import { convertToPurchaseOrderReq, getPurchaseRequisitionDetailsReq, updatePurchaseRequisitionStatusReq } from "../../service/purchaseRequisitionService";
import { setBreadcrumbItems } from "../../store/Breadcrumb/actions";
import { MODULES_ENUM, PERMISSIONS_ENUM, USER_TYPES_ENUM } from "../../utility/constants";
import { formatDate } from "../../utility/formatDate";
import getPaymentTerm from "../../utility/getPaymentTerm";
import OrderStatusRenderer from "../Orders/OrderStatusRenderer";
import PdfComponent from "../Orders/PdfComponent";

const ViewPurchaseRequisition = (props) => {
  const { id } = useParams();
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [itemsData, setItemsData] = useState([]);
  const [requisitionInfo, setRequisitionInfo] = useState({});
  const [status, setStatus] = useState("");
  const [approveModal, setApproveModal] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("published");
  const [paymentTerms, setPaymentTerms] = useState()

  const breadcrumbItems = [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Purchase Requisitions", link: "/purchase-requisitions" },
    { title: "Purchase Requisition", link: "#" },
  ];

  const effectCalled = useRef(false);

  const statusToTextMap = {
    "draft": "Draft",
    "published": "Published",
    "approved": "Approved",
    "rejected": "Rejected",
  }

  async function handlePurchaseRequisitionStatusChange(status) {
    setIsButtonLoading(true);
    try {
      let response
      if (status === 'approved') {
        const purchaseRequisitionId = requisitionInfo._id;
        response = await convertToPurchaseOrderReq(purchaseRequisitionId);
      } else {
        const body = {
          "purchaseRequisitionId": id,
          "status": status
        };
        response = await updatePurchaseRequisitionStatusReq(body);
      }
      if (response.success){
        toast.success(`Purchase Requisition ${statusToTextMap[status]}`);
        setStatus(status);
      } else {
        const { message } = response;
        toast.error(message || "Something went wrong");
      }
    } catch (error) {
        if (error.response && error.response.status === 400) {
            toast.error("Something went wrong")
            // Handle the 400 error here, e.g., show an error message to the user
        } else {
            toast.error("Something went wrong")
        }
    } finally {
        setIsButtonLoading(false);
        setApproveModal(false)
    }
  }

  const approvePurchaseRequisition = async() => {
    setSelectedStatus('approved');
    setApproveModal(true); 
  }

  const rejectPurchaseRequisition = () => {
    setSelectedStatus('rejected');
    setApproveModal(true);
  }

  const handleStatusChange = (event) => {
    const newStatus = event.target.value;
    if (newStatus === 'published') {
      setApproveModal(true);
    }
  };

  const getPurchaseRequisitionDetails = async () => {
    const response = await getPurchaseRequisitionDetailsReq(id);
    const purchaseRequisition = response?.data;

    setItemsData(purchaseRequisition?.items);
    setRequisitionInfo(purchaseRequisition);
    setStatus(purchaseRequisition?.PRStatus);
    if (purchaseRequisition?.PRStatus !== 'draft') {
      setIsDisabled(true);
    }
    else {
      setIsDisabled(false);
    }

    setRequisitionInfo({...purchaseRequisition});
  };

  const getAgreementData = async (id) => {
    try {
      const data = {clientId: id}
      const res = await getAgreement(data);
      
      setPaymentTerms(res.payload.paymentTerms)
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    props.setBreadcrumbItems("Purchase Requisition", breadcrumbItems);
    if (!effectCalled.current) {
      getPurchaseRequisitionDetails();
      effectCalled.current = true;
    }
  }, []);

  useEffect(() => {
    if(requisitionInfo.clientId) {
      getAgreementData(requisitionInfo.clientId);
    }
  }, [requisitionInfo.clientId]);

  const getAddressValue = (branch) => {
    if(!branch?.address) return '';
    return <>
    {branch?.address.addressLine1},<br/>
    {branch?.address.addressLine2 || ''},<br />
    {branch?.address.state}&nbsp;{branch?.address.city}&nbsp;-&nbsp;{branch?.address.pincode}.
    </>;
  }

  return (
    <>
      <div style={{ position: "relative" }}>
        <div
          className="d-flex align-items-center gap-2"
          style={{
            position: "absolute",
            top: -50,
            right: 10,
          }}
        >
          <Modal centered toggle={() => setApproveModal(!approveModal)} isOpen={approveModal}>
            <ApproveConfirm setApproveModal={setApproveModal} handlePurchaseOrderStatusChange={handlePurchaseRequisitionStatusChange} status={selectedStatus} isButtonLoading={isButtonLoading} />
          </Modal>
          {
            status === 'draft' ? (
              <>
                <PdfComponent data={{orderInfo: requisitionInfo, itemsData, paymentTerms }}/>
                <select
                  className="form-select focus-width"
                  name="status"
                  disabled={isDisabled}
                  value={status} // Bind status state to select element
                  onChange={handleStatusChange} // Update status on change
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
                <Link to='edit' className="w-md btn btn-primary">Edit</Link>
              </>)
              :
              <>
                <PdfComponent data={{orderInfo: requisitionInfo, itemsData, paymentTerms }}/>
                {OrderStatusRenderer({ value: status })}
              </>
            // : (<Typography variant="body1" component="span">
            //   <strong>Status:</strong> {status}
            // </Typography>)
          }
          <RequireUserType userType={USER_TYPES_ENUM.CLIENT}>
            <RequirePermission module={MODULES_ENUM.ORDERS} permission={PERMISSIONS_ENUM.CREATE}>
              {status === 'published' && (
                <StyledButton
                  color={"success"}
                  className={"w-md mx-2"}
                  onClick={approvePurchaseRequisition}
                >
                  <CheckCircle className="me-1" />
                  Approve
                </StyledButton>
              )}
            </RequirePermission>
          </RequireUserType>
          <RequireUserType userType={USER_TYPES_ENUM.CLIENT}>
            <RequirePermission module={MODULES_ENUM.ORDERS} permission={PERMISSIONS_ENUM.CREATE}>
              {status === 'published' && (
                <StyledButton
                  color={"danger"}
                  className={"w-md mx-2"}
                  onClick={rejectPurchaseRequisition}
                >
                  <Cancel className="me-1" />
                  Reject
                </StyledButton>
              )}
            </RequirePermission>
          </RequireUserType>
        </div>
      </div >
      <div id='print-container'>
        <Card>
          <CardBody>
            <div className="card-content">
              <div className="details">
                  <h3 className="fw-bolder">
                    {requisitionInfo.clientName}
                  </h3>
                  <p className="m-0">
                    {requisitionInfo.billing?.branchName}
                  </p>
                  <p className="m-0">
                    {getAddressValue(requisitionInfo.billing)}
                  </p>
                  <p className="m-0">{requisitionInfo.billing?.contact}</p>
              </div>
              <div>
                <span className="purchase-order">Purchase Requisition</span>
                <br />
                <span className="purchase-order-no">{requisitionInfo.purchaseRequisitionNumber}</span>
              </div>
            </div>
          </CardBody>
        </Card>

        <Row className="mb-3">
          <div style={{ position: "relative" }}>
            <Row className="equal-height-cards">
              <Col xl="7">
                <Card>
                  <CardBody>
                    <Row className="py-2 border-bottom">
                      <Col>Created Date</Col>
                      <Col>{formatDate(requisitionInfo?.createdAt)}</Col>
                    </Row>
                    <Row className="py-2 border-bottom">
                      <Col>Payment Terms</Col>
                      <Col>
                        {getPaymentTerm(paymentTerms)}
                      </Col>
                    </Row>
                    <Row className="py-2 border-bottom">
                      <Col>Purchase Order No.</Col>
                      <Col>
                        {requisitionInfo?.purchaseOrderNumber ? requisitionInfo?.purchaseOrderNumber : "-"}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col xl="5">
                <Card>
                  <CardBody className="d-flex flex-column gap-2">
                    <div style={{ flex: 1 }} className="border-bottom">
                      <p className="fw-lighter m-0">Vendor Address</p>
                      <h5 className="fw-medium text-uppercase m-0">
                        Bansi Office Solutions Private Limited
                        #1496, 19th Main Road, Opp Park Square Apartment, HSR Layout,
                        Bangalore Karnataka 560102, India
                      </h5>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p className="fw-lighter m-0">Shipping Address</p>
                      <h5 className="fw-medium text-uppercase m-0">{getAddressValue(requisitionInfo.shipping)}</h5>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col xl="8">
                <Card className="mt-3" style={{ height: "fit-content" }}>
                  <CardHeader>Sales Information</CardHeader>
                  <CardBody>
                    <Row className="py-2 border-bottom">
                      <Col xl="4">Item & Description</Col>
                      <Col xl="3">Ordered</Col>
                    </Row>
                    {itemsData && itemsData.map((item, index) => (
                      <Row key={index} className="py-2 border-bottom align-items-center">
                        <Col xl="4">
                          <h6 style={{margin: 0, fontSize: '14px', paddingTop: '3px'}}>{item.itemId.title}</h6>
                          <span style={{margin: 0, fontSize: '12px', color: 'grey', paddingTop: '2px'}}>{item.itemId.Description}</span>
                        </Col>
                        <Col xl="3">{item.quantity}&nbsp;{item.itemUnit}</Col>
                      </Row>
                    ))}
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
            <Col xl="8">
              <Card className="mt-3">
                <CardHeader>Terms & Conditions</CardHeader>
                <CardBody>
                  {requisitionInfo.terms ?
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {requisitionInfo.terms} 
                  </div> 
                  : <span style={{color: 'grey'}}>No Terms & Conditions</span>
                  }
                </CardBody>
              </Card>
            </Col>
          </Row>
          </div>
        </Row>
      </div>
    </>
  );
};

export default connect(null, { setBreadcrumbItems })(ViewPurchaseRequisition);
